import { Spinner, theme } from '@ketch-com/deck'
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider'
import { useChatsByUser } from 'api/assessments/queries/useChatsByUser'
import { useUserMe } from 'api/users/queries/useUserMe'
import { PropsWithChildren, useMemo } from 'react'
import { RefetchOptions } from 'react-query'
import { createCustomContext } from 'utils/hooks/createCustomContext'
import { isRelease } from 'utils/releases'
import { Release } from 'utils/releases/RELEASES'

export interface KetchSendbirdContextProps {
  refetchChatConfigs: (options?: RefetchOptions) => Promise<any>
}

const { Provider, useCustomContext: useKetchSendbirdContext } = createCustomContext<KetchSendbirdContextProps>({
  displayName: 'KetchSendbirdContext',
})

export const ketchColorSet = {
  // See https://sendbird.com/docs/chat/uikit/v3/react/theme-and-style-resources/color-resources
  // for other variables and theme info
  '--sendbird-light-primary-300': theme.palette.heliotrope.main,
  '--sendbird-light-primary-100': theme.palette.Highlight.Disabled,
}

export const KetchSendbirdProviderV2 = ({ children }: PropsWithChildren) => {
  const { data: me } = useUserMe()
  const userId = useMemo(() => me?.ID || '', [me?.ID])

  const isAssessmentQuestionChatEnabled = isRelease(Release.AssesssmentQuestionChat)

  const { data: chatConfigs, refetch: refetchChatConfigs } = useChatsByUser({
    params: { userId },
    enabled: !!userId && isAssessmentQuestionChatEnabled,
  })

  const sendbirdAppId = useMemo(() => (window as any)?.figureheadConfig?.REACT_APP_SENDBIRD_APPLICATION_ID, [])

  const value = useMemo(
    () => ({
      refetchChatConfigs,
    }),
    [refetchChatConfigs],
  )

  return (
    <Provider value={value}>
      {chatConfigs ? (
        <SendbirdProvider
          appId={sendbirdAppId}
          userId={chatConfigs.chat_id}
          colorSet={ketchColorSet}
          accessToken={chatConfigs?.token}
        >
          {children}
        </SendbirdProvider>
      ) : (
        <Spinner size="large" />
      )}
    </Provider>
  )
}

export { useKetchSendbirdContext }
